import { useState } from "react";
import { useContractVersions, ContractVersion } from "@/hooks/useContractAnalytics";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { History, Clock, ChevronRight, FileText } from "lucide-react";
import { format } from "date-fns";
import { ContractVersionComparison } from "./ContractVersionComparison";

interface ContractVersionHistoryProps {
  contractId: string;
  currentVersion?: number;
}

const statusColors: Record<string, string> = {
  draft: "secondary",
  sent: "default",
  viewed: "outline",
  signed: "default",
  expired: "destructive",
};

export function ContractVersionHistory({ contractId, currentVersion }: ContractVersionHistoryProps) {
  const { data: versions, isLoading } = useContractVersions(contractId);
  const [selectedVersion, setSelectedVersion] = useState<ContractVersion | null>(null);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!versions || versions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" />
            Version History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No version history available yet. Changes will be tracked after the first edit.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="h-4 w-4" />
                Version History
              </CardTitle>
              <CardDescription>
                {versions.length} version{versions.length !== 1 ? 's' : ''} recorded
              </CardDescription>
            </div>
            <ContractVersionComparison contractId={contractId} />
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-3">
              {versions.map((version, index) => (
                <div
                  key={version.id}
                  className="relative flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group"
                  onClick={() => setSelectedVersion(version)}
                >
                  {/* Timeline connector */}
                  {index < versions.length - 1 && (
                    <div className="absolute left-[22px] top-[40px] w-0.5 h-[calc(100%+4px)] bg-border" />
                  )}
                  
                  {/* Version indicator */}
                  <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
                    v{version.version_number}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm truncate">
                        {version.title}
                      </span>
                      <Badge 
                        variant={statusColors[version.status] as any || "secondary"}
                        className="text-xs"
                      >
                        {version.status}
                      </Badge>
                      {version.version_number === currentVersion && (
                        <Badge variant="outline" className="text-xs text-primary">
                          Current
                        </Badge>
                      )}
                    </div>
                    
                    {version.change_summary && (
                      <p className="text-xs text-muted-foreground mb-1 line-clamp-1">
                        {version.change_summary}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {format(new Date(version.created_at), "MMM d, yyyy 'at' h:mm a")}
                    </div>
                  </div>
                  
                  <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Version Detail Dialog */}
      <Dialog open={!!selectedVersion} onOpenChange={() => setSelectedVersion(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Version {selectedVersion?.version_number} Details
            </DialogTitle>
            <DialogDescription>
              Created on {selectedVersion && format(new Date(selectedVersion.created_at), "MMMM d, yyyy 'at' h:mm a")}
            </DialogDescription>
          </DialogHeader>
          
          {selectedVersion && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Title</p>
                  <p className="text-sm">{selectedVersion.title}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <Badge variant={statusColors[selectedVersion.status] as any || "secondary"}>
                    {selectedVersion.status}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Signer Name</p>
                  <p className="text-sm">{selectedVersion.signer_name || "Not specified"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Signer Email</p>
                  <p className="text-sm">{selectedVersion.signer_email || "Not specified"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Expiration Date</p>
                  <p className="text-sm">
                    {selectedVersion.expires_at 
                      ? format(new Date(selectedVersion.expires_at), "MMMM d, yyyy")
                      : "No expiration"
                    }
                  </p>
                </div>
              </div>
              
              {selectedVersion.change_summary && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Change Summary</p>
                  <p className="text-sm bg-muted/50 p-3 rounded-lg">
                    {selectedVersion.change_summary}
                  </p>
                </div>
              )}
              
              {selectedVersion.metadata && Object.keys(selectedVersion.metadata).length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Content Snapshot</p>
                  <div className="bg-muted/50 p-3 rounded-lg text-xs font-mono max-h-[200px] overflow-auto">
                    <pre>{JSON.stringify(selectedVersion.metadata, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
